import { ClockCircleOutlined, LockFilled } from '@ant-design/icons';
import { Button, Popconfirm, Tag } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { type WorkflowVersionSummary, workflowsApi } from '@/api/workflows';
import { devNameForOid } from '@/auth/devLogin';
import { useRoles } from '@/auth/useRoles';
import { PageHeader } from '@/components/PageHeader';
import { StatusTag } from '@/components/StatusTag';
import {
  CARD,
  CARD_BODY,
  CARD_HEADER,
  CARD_HEADER_TITLE,
  COLS,
  FIELD_BLOCK,
  FIELD_LABEL,
  FIELD_VALUE,
  INLINE_LABEL,
  INLINE_ROW,
  OUTER_WRAP,
  emptyValue,
} from '@/styles/workflowCardStyles';
import { assigneeOf } from '@/utils/workflowHelpers';

import { useEditorLock } from './useEditorLock';
import { WorkflowSteps } from './WorkflowSteps';

export function WorkflowDetailsPage() {
  const { t, i18n } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { oid } = useRoles();
  // The editor-lock lifecycle (mount-acquire → 20s poll → reclaim → unmount-release)
  // lives in one hook so the page stays declarative. setWorkflow is exposed as
  // patchWorkflow for downstream WorkflowSteps callbacks that need to refresh content.
  const goToDashboard = useCallback(() => navigate('/originator/dashboard'), [navigate]);
  const {
    workflow,
    loading,
    lockBlock,
    reclaim,
    patchWorkflow: setWorkflow,
  } = useEditorLock(id, oid, t, goToDashboard);

  // Load the latest version so the details panel can name the
  // currently-relevant version AND surface the rejection comment
  // inline when the workflow is Rejected. Pending state prefers the
  // Pending row; otherwise the newest row overall wins (Rejected /
  // Approved fall through here). Fire once per workflow id — the
  // details page doesn't mutate version state, so a refetch on
  // template edits isn't needed. Silent on failure — the panel
  // gracefully hides the version / reason rows.
  const [latestVersion, setLatestVersion] = useState<WorkflowVersionSummary | null>(null);
  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    (async () => {
      try {
        const versions = await workflowsApi.listVersions(id);
        if (cancelled) return;
        const pending = versions.find((v) => v.status === 'Pending');
        setLatestVersion(pending ?? versions[0] ?? null);
      } catch {
        if (!cancelled) setLatestVersion(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  if (loading || !workflow) {
    return <div style={{ ...OUTER_WRAP, minHeight: 460 }} />;
  }

  const isFr = i18n.language.startsWith('fr');
  const name = isFr ? workflow.nameFr : workflow.nameEn;
  const description = isFr ? workflow.descriptionFr : workflow.descriptionEn;
  const notProvided = t('workflow.details.notProvided');
  const isLockedByOther = lockBlock !== null;
  const heldByCurrentUser = workflow.lock?.heldByCaller === true;
  const lockExpiresAt = lockBlock?.expiresAt
    ? new Date(lockBlock.expiresAt).toLocaleString(i18n.language)
    : null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <PageHeader
        title={workflow.nameEn || workflow.appName}
        titleSuffix={<StatusTag status={workflow.status} />}
        onBack={() => navigate('/originator/dashboard')}
      />

      {isLockedByOther && (
        // Custom banner — softer warm gradient, filled lock badge, two-column layout.
        // Replaces AntD's <Alert> so we control hierarchy + spacing precisely.
        <div
          role="status"
          aria-live="polite"
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 14,
            padding: '14px 18px',
            borderRadius: 12,
            background: 'linear-gradient(135deg, #fff7ed 0%, #fef3c7 100%)',
            border: '1px solid #fde68a',
            boxShadow: '0 1px 2px rgba(180, 83, 9, 0.08)',
          }}
        >
          {/* Filled lock badge — a small colored chip reads more as a "status" than the
              outlined-icon-in-margin pattern the default AntD Alert uses. */}
          <div
            style={{
              flexShrink: 0,
              width: 36,
              height: 36,
              borderRadius: '50%',
              background: '#f59e0b',
              color: '#fff',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 16,
              boxShadow: '0 1px 2px rgba(180, 83, 9, 0.25)',
            }}
          >
            <LockFilled />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
            <div style={{ fontWeight: 700, color: '#7c2d12', fontSize: 15, lineHeight: 1.3 }}>
              {t('workflow.lock.bannerTitle', {
                name: lockBlock.holderName ?? t('workflow.lock.anotherUser'),
              })}
            </div>
            {lockExpiresAt ? (
              <>
                <div
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    color: '#92400e',
                    fontSize: 13,
                    lineHeight: 1.4,
                  }}
                >
                  <ClockCircleOutlined style={{ fontSize: 12 }} />
                  {t('workflow.lock.bannerExpiry', { time: lockExpiresAt })}
                </div>
                <div style={{ color: '#92400e', fontSize: 13, lineHeight: 1.4 }}>
                  {t('workflow.lock.bannerCantEdit')}
                </div>
              </>
            ) : (
              <div style={{ color: '#92400e', fontSize: 13, lineHeight: 1.4 }}>
                {t('workflow.lock.bannerExpiryUnknown')}
              </div>
            )}
            {/* Owner-only force-release: if the current user owns the workflow but a
                colleague has the lock, give them a way to reclaim it without waiting
                for the 14h TTL. Confirm-popover so an accidental click can't boot
                a colleague mid-edit. */}
            {workflow.ownerOid === oid && (
              <Popconfirm
                title={t('workflow.lock.forceReleaseConfirmTitle')}
                description={t('workflow.lock.forceReleaseConfirmDesc')}
                okText={t('workflow.lock.forceReleaseConfirmOk')}
                cancelText={t('common.cancel')}
                onConfirm={reclaim}
              >
                <Button size="small" style={{ marginTop: 8, alignSelf: 'flex-start' }}>
                  {t('workflow.lock.forceReleaseAction')}
                </Button>
              </Popconfirm>
            )}
          </div>
        </div>
      )}

      <div style={OUTER_WRAP}>
        <div style={COLS}>
          {/* LEFT — Workflow Details */}
          <aside style={CARD}>
            <div style={CARD_HEADER}>
              <h2 style={CARD_HEADER_TITLE}>{t('workflow.details.infoTitle')}</h2>
            </div>
            <div style={CARD_BODY}>
              {/* Order: Name → Description → Created by → Assigned to → APEX/IFDS.
                  Status moved into the page title (rendered as a badge there).
                  Name + Description keep the block layout (label above the value) so
                  longer descriptions wrap naturally; FIELD_LABEL was bumped to match
                  INLINE_LABEL's size so the two row styles read consistently. */}
              <div style={FIELD_BLOCK}>
                <span style={FIELD_LABEL}>{t('workflow.details.fieldName')}</span>
                <span style={FIELD_VALUE}>{emptyValue(name, notProvided)}</span>
              </div>
              <div style={FIELD_BLOCK}>
                <span style={FIELD_LABEL}>{t('workflow.details.fieldDescription')}</span>
                <span style={FIELD_VALUE}>{emptyValue(description, notProvided)}</span>
              </div>
              <div style={INLINE_ROW}>
                <span style={INLINE_LABEL}>{t('workflow.details.createdBy')}:</span>
                {/* Prefer the persisted ownerName; on Dev, fall back to the dev-identity
                    catalog (rows seeded before the field existed); finally fall back to
                    the raw oid so something identifiable always shows. */}
                {workflow.ownerName || devNameForOid(workflow.ownerOid) || workflow.ownerOid}
              </div>
              <div style={INLINE_ROW}>
                <span style={INLINE_LABEL}>{t('workflow.details.assignedTo')}:</span>
                {assigneeOf(workflow, t)}
              </div>
              <div style={INLINE_ROW}>
                <span style={INLINE_LABEL}>{t('workflow.fields.platform')}:</span>
                {workflow.platform}
              </div>
              {/* Dealer-code gating lists are optional and structurally absent for most
                  workflows — hidden entirely when empty rather than showing "Not provided". */}
              {workflow.includedDealerCodes && workflow.includedDealerCodes.length > 0 && (
                <div style={FIELD_BLOCK}>
                  <span style={FIELD_LABEL}>{t('workflow.details.includedDealerCodes')}</span>
                  <span style={FIELD_VALUE}>
                    {workflow.includedDealerCodes.map((code) => (
                      <Tag key={code}>{code}</Tag>
                    ))}
                  </span>
                </div>
              )}
              {workflow.excludedDealerCodes && workflow.excludedDealerCodes.length > 0 && (
                <div style={FIELD_BLOCK}>
                  <span style={FIELD_LABEL}>{t('workflow.details.excludedDealerCodes')}</span>
                  <span style={FIELD_VALUE}>
                    {workflow.excludedDealerCodes.map((code) => (
                      <Tag key={code}>{code}</Tag>
                    ))}
                  </span>
                </div>
              )}
              {/* Version above Status — matches the Approver's Workflow
                  Review layout so the pair reads the same way on both
                  sides. Hidden when we haven't managed to load a
                  version row yet (silent failure keeps the panel
                  clean). */}
              {latestVersion && (
                <div style={INLINE_ROW}>
                  <span style={INLINE_LABEL}>{t('workflow.fields.version')}:</span>
                  {latestVersion.versionLabel}
                </div>
              )}
              <div style={INLINE_ROW}>
                <span style={INLINE_LABEL}>{t('workflow.fields.status')}:</span>
                <StatusTag status={workflow.status} />
              </div>
              {/* Rejection reason inline — the originator sees WHY the
                  workflow was rejected without clicking the badge to
                  open the modal. Only renders when both the template
                  status AND the version carry the rejection state +
                  comment. Mirrors the approver-side treatment. */}
              {workflow.status === 'Rejected' && latestVersion?.rejectionComment && (
                <div
                  style={{
                    marginTop: 4,
                    padding: '10px 12px',
                    background: '#fef2f2',
                    border: '1px solid #fecaca',
                    borderLeft: '3px solid #dc2626',
                    borderRadius: 8,
                    fontSize: 13,
                    color: '#111827',
                    lineHeight: 1.5,
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 600,
                      color: '#b91c1c',
                      textTransform: 'uppercase',
                      letterSpacing: 0.3,
                      marginBottom: 4,
                    }}
                  >
                    {t('workflow.list.rejectionComment')}
                  </div>
                  {latestVersion.rejectionComment}
                </div>
              )}
            </div>
          </aside>

          {/* RIGHT — Workflow Steps (header + list owned by WorkflowSteps).
              `lockedByOther` disables write actions; `lockedHeld` reflects whether we
              acquired the lock so save-time refreshes have a meaningful claim. */}
          <section style={CARD}>
            <div style={{ ...CARD_BODY, gap: 0 }}>
              <WorkflowSteps
                workflow={workflow}
                onChanged={setWorkflow}
                lockedByOther={isLockedByOther}
                lockedHeld={heldByCurrentUser && !isLockedByOther}
              />
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
